import { GoogleOAuthProvider } from '@react-oauth/google';
import React, { FC, Suspense } from 'react';
import { BrowserRouter, matchPath, Route, Routes, useLocation } from 'react-router-dom';
import styled, { createGlobalStyle, css, useTheme } from 'styled-components';
import AppSubscriptionCheck from '../components/appSubscriptionCheck';
import ErrorPageBoundary from '../components/pages/_error';
import NotFoundPage from '../components/pages/_notFound';
import LeftAppSidebar from '../components/widgets/leftAppSidebar/AppLeftSidebar';
import { LoadScripts } from '../components/widgets/scriptsLoader/loadScripts';
import { AppStateProvider } from '../shared/appState';
import config from '../shared/config';
import { PageLoading } from '../shared/globals';
import { backofficeUrl, getJWTFromQueryParams, isVtoPage, routeMatchUrl, setCookie } from '../shared/helpers';
import routes from '../shared/routes';
import { DefaultLayoutContainer } from './defaultLayoutContainer';

const APP_SIDEBAR_WIDTH = 295;

const AppLayoutGlobalStyles = createGlobalStyle<{ $isHeaderHidden: boolean }>`
    ${({ $isHeaderHidden }) =>
        $isHeaderHidden &&
        css`
            body {
                padding-top: 0 !important;
            }
        `}`;

const AppShell = styled.div`
    min-height: 100vh;
`;

const AppContent = styled.div<{ $isMenuHidden: boolean; $noPageFooter: boolean }>`
    min-height: 100vh;
    margin-left: ${(props) => (props.$isMenuHidden ? '0' : `${APP_SIDEBAR_WIDTH}px`)};
    padding: 0 48px ${(props) => (props.$noPageFooter ? '0' : '55px')};
    background-color: #f9f9f9;
`;

const AppLayout: FC = () => {
    const theme = useTheme();
    const location = useLocation();
    const currentRoute = routes.find((x) => {
        const mathWithParams = matchPath(x.url.replaceAll('?', ''), location.pathname);

        // Remove all optioanl params from x.url
        const urlWithNoOptionalParams = x.url.replace(/:\w+\?/g, '');

        const matchNoParams = matchPath(urlWithNoOptionalParams, location.pathname + '/');

        return matchNoParams || mathWithParams;
    });

    const isEmbedded = new URLSearchParams(location.search).get('embedded') === 'true';
    const isHeaderHidden = (isEmbedded || currentRoute?.isHeaderHidden || theme.additionalProperties?.isToolbarHidden) ?? false;
    const isMenuHidden = (isEmbedded || currentRoute?.isMenuHidden || theme.additionalProperties?.isSidebarHidden) ?? false;
    const forceTools = currentRoute?.forceTools ?? false;
    const noPageFooter = currentRoute?.noPageFooter ?? false;

    return (
        <>
            <AppLayoutGlobalStyles $isHeaderHidden={isHeaderHidden} />
            <AppShell>
                {!isMenuHidden && <LeftAppSidebar isMenuHidden={isMenuHidden} />}
                <AppContent $isMenuHidden={isMenuHidden} $noPageFooter={noPageFooter}>
                    {/* We remove all :params to avoid losing the context for some sub pages like products */}
                    <Suspense fallback={<PageLoading />}>
                        <Routes>
                            {routes.map((route, index) => {
                                return (
                                    <Route
                                        key={index}
                                        path={routeMatchUrl(route.url)}
                                        element={
                                            <ErrorPageBoundary key={route.url.replace(/:\w+/g, '')}>{React.createElement(route.component)}</ErrorPageBoundary>
                                        }
                                    />
                                );
                            })}
                            <Route path='*' element={<NotFoundPage />} />
                        </Routes>
                    </Suspense>
                </AppContent>
            </AppShell>
        </>
    );
};

const localizationsScripts = [
    config.publicPath + 'assets/libs/gettext/gettext.js',
    config.localizationsUrl + config.culture.toLowerCase().substring(0, 2) + '/admin.js'
];

const DefaultLayout: FC = () => {
    if (!config.token) {
        const jwt = getJWTFromQueryParams();
        if (isVtoPage() && jwt) {
            config.token = jwt;
            setCookie('zakeke_token', jwt, 365);
        } else {
            window.location.href = `${backofficeUrl('/Login')}`;
            return null;
        }
    }

    return (
        <LoadScripts scriptUrls={localizationsScripts} fallback={<PageLoading />}>
            <GoogleOAuthProvider clientId={config.googleLoginClientID}>
                <BrowserRouter>
                    <Suspense fallback={<PageLoading />}>
                        <AppStateProvider>
                            <AppSubscriptionCheck>
                                <DefaultLayoutContainer>
                                    <AppLayout />
                                </DefaultLayoutContainer>
                            </AppSubscriptionCheck>
                        </AppStateProvider>
                    </Suspense>
                </BrowserRouter>
            </GoogleOAuthProvider>
        </LoadScripts>
    );
};

export default DefaultLayout;
